import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FORM_IDS, INTEGRATION_CONSTANTS, QBD_LOADING_STATES, QBD_LOADING_STATES_MESSAGE_MAP } from "../../constants";
import { useDispatch, useSelector } from "react-redux";
import { componentKey, setIsQbInactiveVendor } from "./WebConnectorSlice";
import { getFormSetting, getIsInactiveVendors, getLoadImportData, getWebConnectorSyncData, qbWebConnectorDownload, qbWebConnectorUserLogin, updateInactiveVendor } from "./WebConnectorSaga";
import { componentKey as authenticationComponentKey } from "../../../../authentication/AuthenticationSlice";
import { addNotifications } from "../../../../toaster/ToasterPortalSlice";
import { SOURCE_CONSTANTS } from "../../constants";
import ImportDataGridView from "../../../../components/importdatagridview/ImportDataGridView";
import Button from "../../../../../components/button/Button";
import { BUTTON_TYPE } from "../../../../../components/button/constants";
import importSectionStyles from "../../../ImportSection.styles";
import useGarbageCollector from "../../../../../../libs/garbagecollector/garbageCollectorHooks";
import Icons from "../../../../../components/icons/Icons";
import { VEC_ICON_NAME } from "../../../../../components/icons/constants";
import Heading from "../../../../../components/heading/Heading";
import BasicCard from "../../../../../components/card/basiccard/BasicCard";
import qbdStyles from "../Qbd.styles";
import Breadcrumbs from "../../../../../components/breadcrumbs/Breadcrumbs";
import { INTEGRATION_ROUTES } from "../../../../constants/constants";
import Loadable from "../../../../../components/loadable/Loadable";
import CheckboxWLabel from "../../../../../components/checkbox/checkboxwlabel/CheckboxWLabel";
import ConfirmationPopup from "../../../../../components/popup/confirmationpopup/ConfirmationPopup";

export default function WebConnector() {
    useGarbageCollector(componentKey);
    const [payerId, setPayerId] = useState("");
    const [formType, setFormType] = useState("");
    const [taxYear, setTaxYear] = useState("");
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const { loadingState, isWebConnectorAuthenticated, importData, isImportDataGridViewLoaded, formSettings, webConnectorSyncData, isQbInactiveVendor } = useSelector(state => state[componentKey]);
    const { isUserAuthenticated } = useSelector(state => state[authenticationComponentKey]);
    const [queryParams, setQueryParams] = useSearchParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();


    useEffect(() => {
        const { PAYER_ID_PARAM, FORM_TYPE_PARAM, TAXYEAR_PARAM } = INTEGRATION_CONSTANTS;
        if (queryParams.has(PAYER_ID_PARAM)) {
            setPayerId(queryParams.get(PAYER_ID_PARAM));
        }
        if (queryParams.has(FORM_TYPE_PARAM)) {
            setFormType(queryParams.get(FORM_TYPE_PARAM));
        }
        if (queryParams.has(TAXYEAR_PARAM)) {
            setTaxYear(queryParams.get(TAXYEAR_PARAM));
        }
        setQueryParams(queryParams);

    }, [queryParams]);

    useEffect(() => {
        if (payerId && formType && taxYear) {
            queryParams.forEach((_, key) => {
                queryParams.delete(key);
            });
        }
    }, [payerId, formType, taxYear])

    useEffect(() => {
        dispatch(getIsInactiveVendors());
    }, [])

    useEffect(() => {
        if (payerId) {
            dispatch(qbWebConnectorUserLogin(payerId))
        }
    }, [payerId])

    useEffect(() => {
        if (importData) {
            dispatch(getFormSetting({ formId: formType, year: taxYear }));
        }
    }, [importData]);

    useEffect(() => {
        if (isWebConnectorAuthenticated && isUserAuthenticated) {
            if (payerId && taxYear && formType) {
                dispatch(getLoadImportData(
                    {
                        integrationImportRequestDto: {
                            TaxYear: taxYear,
                            FormName: formType,
                            PayerId: payerId,
                            Source: SOURCE_CONSTANTS.QBD_WBCONNECTOR_SOURCE
                        }
                    }));
            }
            else {
                dispatch(addNotifications({ heading: "Error", message: "Check email & click latest link! ", variant: TOASTER_VARIANT.DANGER }))
            }
        }
        else if (!isUserAuthenticated) {
            navigate('/signin');
        }

    }, [isWebConnectorAuthenticated])

    useEffect(() => {
        if (webConnectorSyncData) {
            const { PayerId, TaxYear, FormName } = webConnectorSyncData;
            queryParams.set('PayerId', PayerId);
            queryParams.set('TaxYear', TaxYear);
            queryParams.set('FormType', FormName);
            setQueryParams(queryParams);
        }
    }, [webConnectorSyncData]);

    const breadcrumbItems = [
        { text: 'Home', link: '/' },
        { text: 'Import', link: `/${INTEGRATION_ROUTES.IMPORT}` },
        { text: 'qbd', link: `/${INTEGRATION_ROUTES.IMPORT}/${INTEGRATION_ROUTES.QBD}` },
        { text: 'webconnector', link: `/${INTEGRATION_ROUTES.IMPORT}/${INTEGRATION_ROUTES.WEB_CONNECTOR}` },
    ];

    const handleCheckBoxChange = (e) => {
        setIsPopupOpen(true);
        setIsChecked(e.target.checked);
    }

    return (
        <>
            <Breadcrumbs items={breadcrumbItems} />
            {(formSettings && taxYear && formType && isImportDataGridViewLoaded) ? (

                <ImportDataGridView
                    isStateFilingApplicable={false}
                    filingForCorrected={false}
                    formSettings={formSettings}
                    formName={formType}
                    formId={
                        (() => {
                            switch (formType) {
                                case INTEGRATION_CONSTANTS.NEC_FORM:
                                    return FORM_IDS.$1099NEC;
                                case INTEGRATION_CONSTANTS.MISC_FORM:
                                    return FORM_IDS.$1099MISC;
                                default:
                                    return null;
                            }
                        })()
                    }
                    taxYear={taxYear}
                />
            ) : (
                <>
                    <Loadable
                        currentState={loadingState.state}
                        loadingStates={QBD_LOADING_STATES.PAGE_LOADING}
                        messageMap={QBD_LOADING_STATES_MESSAGE_MAP}
                        isNewLoadable={true}
                    >
                        <Heading type="H2">
                            QuickBooks Web Connector Instructions
                        </Heading>

                        <BasicCard customStyles={qbdStyles.paddingCard}>
                            <div style={qbdStyles.cardContainer}>
                                <div>
                                    <Heading type="H3">1. Download and Install Your Tax1099 Web Connector File</Heading>
                                    <p>
                                        Make sure QuickBooks Desktop is running and the correct company is open as we will associate the sync with that company.
                                    </p>
                                    <p>
                                        When you open your Web Connector File, QuickBooks Desktop will walk you through the Web Connector installation process.
                                    </p>

                                    <Button
                                        type={BUTTON_TYPE.PRIMARY}
                                        onClickCb={() => dispatch(qbWebConnectorDownload())}
                                        style={importSectionStyles.classicButtonStyle}
                                    >
                                        <Icons iconName={VEC_ICON_NAME.DOWNLOAD_ICON} />
                                        <span style={qbdStyles.labelStyle1}>Click Here To</span>
                                        <br />
                                        <span style={qbdStyles.labelStyle2}>Download Web Connector File</span>
                                    </Button>
                                </div>
                                <div>
                                    <div>
                                        If you did not receive a web connector sync confirmation email, click the button below to proceed.
                                    </div>
                                    <Button
                                        type={BUTTON_TYPE.LIGHT_WITH_NO_BORDER}
                                        onClickCb={() => dispatch(getWebConnectorSyncData())}
                                        style={importSectionStyles.classicButtonStyle}
                                    >
                                        Proceed Without Confirmation Email
                                    </Button>
                                </div>
                                <div>
                                    <CheckboxWLabel
                                        checked={isQbInactiveVendor}
                                        label={" Check this box if you want to include inactive vendors in the import process."}
                                        onChangeCb={(e) => handleCheckBoxChange(e)}
                                        containerCustomStyle={qbdStyles.uploadContainer}
                                    />
                                </div>
                                <ConfirmationPopup
                                    title={""}
                                    bodyText={" Do you want to include inactive vendors in the import process?"}
                                    iconName={VEC_ICON_NAME.INFO_ICON}
                                    isOpen={isPopupOpen}
                                    confirmationButtonProps={{
                                        onConfirmationCb: () => {
                                            dispatch(updateInactiveVendor({
                                                model: {
                                                    isInactiveVendors: isChecked
                                                }
    
                                            }));
                                            setIsPopupOpen(false)
                                        },
                                        confirmationButtonText: `Ok`,
                                        confirmationButtonType: BUTTON_TYPE.PRIMARY,
                                    }}
                                    cancelButtonProps={{
                                        onCancelCb: () => {
                                            setIsPopupOpen(false)
                                        },
                                        cancelButtonText: `Cancel`,
                                        cancelButtonStyles: {},
                                        cancelButtonType: BUTTON_TYPE.PRIMARY_LIGHT_WITH_BORDER,
                                    }}
                                    showClose={false}
                                />
                            </div>
                        </BasicCard>
                    </Loadable>
                </>
            )
            }
        </>
    )
}